<template>
  <StudyProgramCollapsible
    v-for="[studyProgram, studyProgramLinks] in linksGroupedByStudyProgram"
    :key="studyProgram.id"
    v-model="studyProgramExpanded[studyProgram.id]"
  >
    <template v-slot:title>
      <Heading2>
        {{ formatStudyProgramDisplayName(studyProgram) }}
      </Heading2>
    </template>
    <template v-slot:items>
      <ActivityCard
        v-for="link in studyProgramLinks"
        :key="link.id"
        :link="link"
        :showType
        :activityLocations
      ></ActivityCard>
    </template>
  </StudyProgramCollapsible>
</template>

<script setup lang="ts">
import ActivityCard from "@/views/components/activity-card/ActivityCard.vue";
import StudyProgramCollapsible from "@/views/components/by-study-programs/StudyProgramCollapsible.vue";
import {
  linkMatchesLinkInFilter,
  linkMatchesStudyProgramInFilter,
  studyProgramMatchesInFilter,
  studyProgramMatchesQuery,
} from "@/views/components/filters/filter-functions";
import {
  PublicActivityLocationDTO,
  PublicActivityRegistrationLinkDTO,
  PublicStudyProgramDto,
} from "lib/eduConfigurationServiceClient";
import { computed, ref } from "vue";

const props = defineProps<{
  showType: boolean;
  activityLocations?: PublicActivityLocationDTO[];
  links: PublicActivityRegistrationLinkDTO[];
  studyPrograms: PublicStudyProgramDto[];
  searchQuery?: string;
  linkFilter?: PublicActivityRegistrationLinkDTO[];
  studyProgramFilter?: PublicStudyProgramDto[];
}>();

const studyProgramExpanded = ref<Record<string, boolean>>({});

const linksByFiltered = computed(() => {
  return props.links
    .filter(linkMatchesLinkInFilter(props.linkFilter))
    .filter(linkMatchesStudyProgramInFilter(props.studyProgramFilter));
});

const studyProgramsByFiltered = computed(() => {
  return props.studyPrograms
    .filter(studyProgramMatchesQuery(props.searchQuery))
    .filter(studyProgramMatchesInFilter(props.studyProgramFilter))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
});

const linksGroupedByStudyProgram = computed(() => {
  const map = new Map<
    PublicStudyProgramDto,
    PublicActivityRegistrationLinkDTO[]
  >();

  studyProgramsByFiltered.value.forEach((studyProgram) => {
    const filteredLinks = linksByFiltered.value.filter((link) =>
      link.studyProgramIds.includes(studyProgram.id),
    );
    if (filteredLinks.length > 0) {
      map.set(studyProgram, filteredLinks);
    }
  });

  return map;
});

function formatStudyProgramDisplayName(studyProgram: PublicStudyProgramDto) {
  return [studyProgram.name, studyProgram.mode, studyProgram.language]
    .filter((s) => !!s)
    .join(" – ");
}
</script>
